import React, { useEffect, useMemo, useState } from 'react';
import api from '../../utils/api';

const STATUS_COLORS = {
  succeeded: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  processing: 'bg-blue-100 text-blue-700',
  failed: 'bg-red-100 text-red-700',
  refunded: 'bg-gray-200 text-gray-700',
  partially_refunded: 'bg-amber-100 text-amber-800'
};

const STATUS_LABELS = {
  succeeded: 'Succeeded',
  pending: 'Pending',
  processing: 'Processing',
  failed: 'Failed',
  refunded: 'Refunded',
  partially_refunded: 'Partial refund'
};

// Inclusive start-of-day / end-of-day in the user's local zone, returned as ISO.
const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const endOfDay = (d) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};

// Compute { from, to } Date pairs for each preset.
const getPresetRange = (preset) => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (preset) {
    case 'this_month':
      return { from: startOfDay(new Date(y, m, 1)), to: endOfDay(now) };
    case 'last_month': {
      const start = new Date(y, m - 1, 1);
      const end = new Date(y, m, 0);
      return { from: startOfDay(start), to: endOfDay(end) };
    }
    case 'last_3_months':
      return { from: startOfDay(new Date(y, m - 2, 1)), to: endOfDay(now) };
    case 'ytd':
      return { from: startOfDay(new Date(y, 0, 1)), to: endOfDay(now) };
    case 'all_time':
    default:
      return { from: null, to: null };
  }
};

const PRESETS = [
  { id: 'this_month', label: 'This month' },
  { id: 'last_month', label: 'Last month' },
  { id: 'last_3_months', label: 'Last 3 months' },
  { id: 'ytd', label: 'Year to date' },
  { id: 'all_time', label: 'All time' }
];

const fmtMoney = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtDate = (d) => new Date(d).toLocaleDateString();

const PaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState({ gross: 0, refunds: 0, net: 0, fees: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState('this_month');
  const [downloading, setDownloading] = useState(false);

  const range = useMemo(() => getPresetRange(preset), [preset]);

  useEffect(() => {
    let cancelled = false;
    const fetchPayments = async () => {
      setLoading(true);
      try {
        const params = {};
        if (range.from) params.from = range.from.toISOString();
        if (range.to) params.to = range.to.toISOString();
        const { data } = await api.get('/payments', { params });
        if (cancelled) return;
        setPayments(data.payments || []);
        setStats(data.stats || { gross: 0, refunds: 0, net: 0, fees: 0, count: 0 });
      } catch (err) {
        if (!cancelled) console.error('Failed to load payments', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPayments();
    return () => { cancelled = true; };
  }, [range.from, range.to]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const params = {};
      if (range.from) params.from = range.from.toISOString();
      if (range.to) params.to = range.to.toISOString();
      const res = await api.get('/payments/export.csv', { params, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/);
      a.download = match ? match[1] : 'payments.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      console.error('CSV download failed', err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Payments</h1>
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading || loading || payments.length === 0}
          className="inline-flex items-center gap-2 border border-gray-300 px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
          </svg>
          {downloading ? 'Preparing...' : 'Download CSV'}
        </button>
      </div>

      {/* Period selector */}
      <div className="flex flex-wrap gap-2 mb-6">
        {PRESETS.map(p => (
          <button
            key={p.id}
            type="button"
            onClick={() => setPreset(p.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              preset === p.id
                ? 'bg-brand-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
          <div className="text-sm text-gray-500">Gross revenue</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{fmtMoney(stats.gross)}</div>
          <div className="text-xs text-gray-400 mt-1">{stats.count} payment{stats.count === 1 ? '' : 's'}</div>
        </div>
        <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
          <div className="text-sm text-gray-500">Refunds</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">
            {stats.refunds > 0 ? <span className="text-red-600">&minus;{fmtMoney(stats.refunds)}</span> : fmtMoney(0)}
          </div>
          <div className="text-xs text-gray-400 mt-1">From this period</div>
        </div>
        <div className="bg-white p-5 rounded-xl border border-brand-200 shadow-sm bg-brand-50/30">
          <div className="text-sm text-gray-500">Net revenue</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{fmtMoney(stats.net)}</div>
          <div className="text-xs text-gray-400 mt-1">Gross minus refunds</div>
        </div>
      </div>

      {/* Payment list */}
      {loading ? (
        <div className="bg-white p-8 rounded-xl border border-gray-100 shadow-sm text-center text-gray-400 text-sm">
          Loading payments...
        </div>
      ) : payments.length === 0 ? (
        <div className="bg-white p-8 rounded-xl border border-gray-100 shadow-sm text-center text-gray-500">
          No payments in this period.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Student</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Course</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Amount</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Refunded</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {payments.map(p => {
                const isRefund = p.status === 'refunded' || p.status === 'partially_refunded';
                return (
                  <tr key={p._id} className={`hover:bg-gray-50 ${isRefund ? 'opacity-80' : ''}`}>
                    <td className="px-4 py-3 text-gray-900">{p.student?.name || p.student?.email || '—'}</td>
                    <td className="px-4 py-3 text-gray-700">{p.course?.name || '—'}</td>
                    <td className="px-4 py-3 text-gray-500 capitalize">{p.type?.replace('_', ' ')}</td>
                    <td className="px-4 py-3 text-right text-gray-900">{fmtMoney(p.amount)}</td>
                    <td className="px-4 py-3 text-right">
                      {p.refundedAmount > 0 ? (
                        <span className="text-red-600 font-medium">&minus;{fmtMoney(p.refundedAmount)}</span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[p.status] || 'bg-gray-100 text-gray-500'}`}>
                        {STATUS_LABELS[p.status] || p.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500 whitespace-nowrap">
                      {fmtDate(p.createdAt)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PaymentsPage;
